import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { useTheme } from 'components/core/theme-provider';

/**
 * GDPRConsentDialog Component
 *
 * A simple GDPR consent dialog for basic privacy consent.
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {(open: boolean) => void} onOpenChange - Callback when dialog state changes
 * @param {() => void} onConsent - Callback when user provides consent
 * @param {() => void} onDecline - Callback when user declines consent
 * @returns {JSX.Element} GDPR consent dialog component
 */

interface GDPRConsentDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConsent: () => void;
    onDecline: () => void;
}

export const GDPRConsentDialog: React.FC<GDPRConsentDialogProps> = ({
    open,
    onOpenChange,
    onConsent,
    onDecline,
}) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

    const handleAgree = () => {
        if (!hasAcceptedTerms) return;
        onConsent();
        onOpenChange(false);
    };

    const handleDecline = () => {
        onDecline();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                hideClose={true}
                className="max-w-lg"
                aria-describedby="gdpr-consent-description"
            >
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold">
                        {t('GDPR_CONSENT_TITLE', 'Consents')}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <p className={theme === 'dark' ? "text-sm text-gray-200  leading-relaxed" : "text-sm text-gray-700 leading-relaxed"}>
                        {t('GDPR_CONSENT_TEXT_1', 'The user information you provide will only be used for the EagleGPT platform, its operation and its ongoing optimisation. The extensive protection of your data is regulated and secured by our General Terms and Conditions.')}
                    </p>

                    <p className={theme === 'dark' ? "text-sm text-gray-200 leading-relaxed" : "text-sm text-gray-700 leading-relaxed"}>
                        {t('GDPR_CONSENT_TEXT_2', 'Your e-mail address is used exclusively for registration, authentication and communication on the EagleGPT platform. You will only receive emails about this in connection with necessary functions and relevant updates.')}
                    </p>

                    <p className={theme === 'dark' ? "text-sm text-gray-200 leading-relaxed" : "text-sm text-gray-700 leading-relaxed"}>
                        {t('GDPR_CONSENT_TEXT_3', 'All documents available in the application are only intended for the specified and usual purposes and are to be used expressly only for these.')}
                    </p>

                    <p className={theme === 'dark' ? "text-sm text-gray-200 leading-relaxed" : "text-sm text-gray-700 leading-relaxed"}>
                        {t('GDPR_CONSENT_TEXT_4', 'We use cookies on this site to facilitate your use of this website and to provide features.')}
                    </p>

                    <p className={theme === 'dark' ? "text-sm text-gray-200 leading-relaxed" : "text-sm text-gray-700 leading-relaxed"}>
                        {t('GDPR_CONSENT_TEXT_5', 'If you do not agree to our terms and conditions, further use of EagleGPT is not possible.')}
                    </p>

                    <div className="flex items-start gap-3 mt-6">
                        <Checkbox
                            id="accept-terms"
                            checked={hasAcceptedTerms}
                            onCheckedChange={(checked) => setHasAcceptedTerms(!!checked)}
                            className="mt-0.5"
                        />
                        <label
                            htmlFor="accept-terms"
                            className={theme === 'dark' ? "text-sm text-gray-200 cursor-pointer leading-relaxed" : "text-sm text-gray-700 cursor-pointer leading-relaxed"}
                        >
                            {t('GDPR_ACCEPT_TERMS', 'I accept the')}{' '}
                            <a
                                href="https://www.be-brave.ch/datenschutzbestimmungen/"
                                target="_blank"
                                rel="noreferrer"
                                className="text-yellow-600 hover:text-yellow-700 underline"
                            >
                                {t('GDPR_GENERAL_TERMS', 'Terms and Conditions')}
                            </a>
                        </label>
                    </div>
                </div>

                <DialogFooter className="gap-3 mt-6">
                    <Button
                        variant="outline"
                        onClick={handleDecline}
                        className="w-full"
                    >
                        {t('GDPR_NOT_AGREED', 'NOT AGREED')}
                    </Button>
                    <Button
                        onClick={handleAgree}
                        disabled={!hasAcceptedTerms}
                        className="w-full"
                    >
                        {t('GDPR_AGREED', 'AGREED')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}; 